import React from 'react';

/**
 * Survey / Grid control card, shown as a map overlay while the operator draws a
 * survey area. Presentational only — all state (polygon, params, preview) lives
 * in MissionPlanner. Mirrors ArduPilot Mission Planner's Survey(Grid) controls:
 * line spacing, altitude and sweep angle, with a live coverage estimate.
 */
function SurveyGridPanel({
  params,            // { overlap, hfovDeg, altitude, angleDeg } — spacing derived from these
  onParamChange,     // (key, value) => void
  vertexCount,       // number of polygon vertices placed
  preview,           // { stats: { lineCount, waypointCount, lengthMeters } } | null
  onGenerate,
  onUndo,
  onClear,
  onCancel,
}) {
  const ready = vertexCount >= 3;

  return (
    <div style={panelStyle}>
      <div style={headerRow}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 24, height: 24, borderRadius: 6, background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>grid_on</span>
          </span>
          SURVEY AREA
        </span>
        <button onClick={onCancel} style={closeBtn} title="Exit survey mode">
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
        </button>
      </div>

      <div style={hintStyle}>
        {ready
          ? `${vertexCount} points — adjust grid below`
          : `Click the map to outline the area (${vertexCount}/3 min)`}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--textSecondary)' }}>
            <span>Side overlap</span>
            <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{params.overlap}%</span>
          </span>
          <input type="range" min={0} max={90} step={5} value={params.overlap}
            onChange={(e) => onParamChange('overlap', e.target.value)}
            style={{ width: '100%', accentColor: 'var(--accent)' }} />
        </label>
        <Field label="Camera HFOV (°)" value={params.hfovDeg} min={10} max={170} step={1}
          onChange={(v) => onParamChange('hfovDeg', v)} />
        <Field label="Altitude AGL (m)" value={params.altitude} min={1} step={1}
          onChange={(v) => onParamChange('altitude', v)} />
        <Field label="Sweep angle (°)" value={params.angleDeg} min={0} max={359} step={5}
          onChange={(v) => onParamChange('angleDeg', v)} />
      </div>

      {preview && (
        <div style={statsBox}>
          <Stat label="Spacing" value={`${preview.stats.spacing} m`} />
          <Stat label="Lines" value={preview.stats.lineCount} />
          <Stat label="Waypoints" value={preview.stats.waypointCount} />
          <Stat label="Path" value={`${(preview.stats.lengthMeters / 1000).toFixed(2)} km`} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
        <button onClick={onUndo} disabled={vertexCount === 0} style={secondaryBtn(vertexCount === 0)}>Undo</button>
        <button onClick={onClear} disabled={vertexCount === 0} style={secondaryBtn(vertexCount === 0)}>Clear</button>
        <button onClick={onGenerate} disabled={!preview} style={primaryBtn(!preview)}>Generate Grid</button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, min, max, step }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ fontSize: 11, color: 'var(--textSecondary)' }}>{label}</span>
      <input
        type="number"
        className="wp-field"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 80 }}
      />
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ textAlign: 'center', flex: 1 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>{value}</div>
      <div style={{ fontSize: 9, color: 'var(--textMuted)', textTransform: 'uppercase' }}>{label}</div>
    </div>
  );
}

const panelStyle = {
  position: 'absolute',
  top: 16,
  right: 16,
  zIndex: 1200,
  width: 250,
  background: 'var(--bgSecondary)',
  border: '1px solid var(--borderSecondary)',
  borderTop: '2px solid var(--accent)',
  borderRadius: 12,
  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
  padding: 14,
  fontFamily: "'JetBrains Mono', monospace",
};

const headerRow = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  color: 'var(--accent)', fontWeight: 700, fontSize: 13,
  textTransform: 'uppercase', letterSpacing: '1px',
};

const hintStyle = { fontSize: 10, color: 'var(--textMuted)', marginTop: 6 };

const statsBox = {
  display: 'flex', gap: 4, marginTop: 12, padding: '8px 4px',
  background: 'rgba(0, 0, 0, 0.25)', border: '1px solid var(--borderSecondary)',
  borderRadius: 8,
};

const closeBtn = {
  background: 'none', border: 'none', color: 'var(--textSecondary)',
  cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center',
};

const secondaryBtn = (disabled) => ({
  flex: 1, padding: '7px 0', background: 'transparent',
  color: disabled ? 'var(--textMuted)' : 'var(--textSecondary)',
  border: '1px solid var(--borderSecondary)', borderRadius: 8,
  cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 600,
  fontFamily: "'JetBrains Mono', monospace",
});

const primaryBtn = (disabled) => ({
  flex: 1.6, padding: '7px 0',
  background: disabled ? 'var(--bgTertiary)' : 'var(--accent)',
  color: disabled ? 'var(--textMuted)' : '#06222b',
  border: '1px solid ' + (disabled ? 'var(--borderSecondary)' : 'var(--accent)'), borderRadius: 8,
  cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 700,
  textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace",
});

export default SurveyGridPanel;
